import { ReactElement, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Button } from '@Hotels-at-Home/hah-fe-core/dist/components';
import BannerWrapper from '@Hotels-at-Home/hah-fe-core/dist/components/BannerWrapper';
import { IGetPromotionResponse } from '@Hotels-at-Home/hah-fe-core/dist/types';
// import BmsmChart from '~/ui/components/common/Banner/BmsmChart';
import Modal from '@Hotels-at-Home/hah-fe-core/dist/components/Modal';
import { getCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { heroSection } from '~/constants/siteConfig';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const isWinterHero = heroSection.season === 'winter';
const heroDesktopSrc = `/images/homepage/heroSection/kem-hero-bg-dsktp${
  isWinterHero ? '-winter' : ''
}.webp`;
const heroMobileSrc = `/images/homepage/heroSection/kem-hero-bg-mob${
  isWinterHero ? '-winter' : ''
}.webp`;

interface IProps {
  promotion: IGetPromotionResponse;
}

const HeroSection = ({ promotion }: IProps): ReactElement => {
  const { route } = useRouter();
  const { t } = useTranslation('', { keyPrefix: '$local.$hpCopy' });

  const [showBMSMModal, setShowBMSMModal] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const openPopup = () => {
    setShowBMSMModal(true);
  };

  const closePopup = () => {
    setShowBMSMModal(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      openPopup();
    }
  };

  let RenderBanners;

  const [shouldHideBanner, setShouldHideBanner] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const promoTag = getCookie('promo-tag');
    const hideForPromoTag =
      promoTag === 'air-france-x-sofitel' || promoTag === 'accorhotels-bienvenue';
    setShouldHideBanner(hideForPromoTag);
  }, []);

  const isPostsPage = route.includes('/posts');

  if (shouldHideBanner) {
    RenderBanners = null;
  } else if (promotion?.promo_is_active && promotion?.promo_type !== 'bmsm') {
    RenderBanners = <BannerWrapper promotion={promotion} />;
  } else if (promotion?.promo_is_active && promotion?.promo_type === 'bmsm') {
    RenderBanners = (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-pressed="false"
          className="bmsm-banner-wrapper"
          onClick={openPopup}
          onKeyDown={handleKeyDown}
        >
          <BannerWrapper promotion={promotion} />
        </div>
        <Modal
          open={showBMSMModal}
          maxWidth="sm"
          className="base-banner-lightbox bmsm-lightbox"
          onClose={closePopup}
        >
          {/* <div className="content">
            <BmsmChart />
          </div> */}
        </Modal>
      </>
    );
  } else {
    RenderBanners = null;
  }

  const shouldRenderBanners = !isPostsPage || isClient;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section className="base-section hero-section main-hp hp-section">
      {/* Main Hero Banner – HTML adapted from Vue (catUrl('bed'), img-wrap, stack) */}
      <div className="main-hp">
        <div className="img-wrap">
          <picture>
            <source srcSet={heroMobileSrc} media="(max-width: 768px)" />
            <img src={heroDesktopSrc} alt={t('heroHeading')} width="1920" height="640" />
          </picture>
          {isWinterHero && (
            <div className="cinema-gif" id="hero-cinema-gif">
              <picture>
                <source srcSet="/images/homepage/heroSection/fill.gif" media="(max-width: 768px)" />
                <img
                  src="/images/homepage/heroSection/kem-hero-overlay.gif"
                  alt={t('heroHeading')}
                  loading="lazy"
                  width="1920"
                  height="640"
                />
              </picture>
            </div>
          )}
        </div>
        <div className="stack">
          <p className="eyebrow">{t('heroEyebrow')}</p>
          <h1 className="main-section-title">{t('heroHeading')}</h1>
          <p className="txt">{t('heroSubHeading')}</p>
          <Button ariaLabel={t('heroCta')} href={t('heroLink')} id="HP_hero_bed">
            {t('heroCta')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
